/**
 * Consume the ?billing=success|cancel return param that Stripe Checkout
 * redirects back with (set by create_checkout_session's success/cancel URLs).
 *
 * Consumed once, same semantics as the pendingTier stash: the param is
 * stripped via history.replaceState immediately, so a refresh doesn't
 * re-toast. Lives in its own module (not App.tsx) so it is unit-testable —
 * importing App.tsx in jest dies on the pre-existing react-vega ESM issue.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../components/Toast";

export function useBillingReturn(onSuccess: () => void) {
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const url = new URL(window.location.href);
    const billing = url.searchParams.get("billing");
    if (billing !== "success" && billing !== "cancel") return;

    // Strip first so a re-render/refresh never re-toasts.
    url.searchParams.delete("billing");
    window.history.replaceState(null, "", url.toString());

    if (billing === "success") {
      toast.success(t("settings.billingSuccess"));
      onSuccess();
    } else {
      toast.info(t("settings.billingCancelled"));
    }
    // Consumed-once on mount by design; toast/t/onSuccess are intentionally
    // not deps (re-running on their identity churn would be a re-toast bug).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useBillingReturn;
